import type {
  MediaclipAutoCrop,
  MediaclipBeautyshotPhoto,
} from 'shared/types/mediaclip/actions/CreateMediaclipBeautyshot';
import {
  ImageOptions,
  OriginalPhotoInfo,
  TransformedPhotoInfo,
  getImageVersions,
  convertGoogleImageData,
} from 'helpers/imageData';
import { getCustomizationData, setCustomizationData } from 'helpers/utils/customizationStorage';
import { sdk } from 'sdk';

export const MEDIACLIP_SCRIPT_URL = 'https://static.mediacliphub.com/scripts/hub.min.js';
export const MEDIACLIP_AUTH_SCHEME = 'Bearer';

type BoolRef = { current: boolean };
/** The hub passes its current authorization to `renewTokenCallback` as arg 1. */
export type MediaclipAuthorization = { scheme: string; token: string };
export type MediaclipHubOptions = {
  renewTokenCallback?: (authorization?: MediaclipAuthorization) => Promise<{ token?: string; redirectUrl?: string }>;
  onError?: (err: unknown) => void;
};
interface ProjectThumbnailSrc {
  promise: Promise<string>;
  done: (callback: (thumbnailUrl: string) => void) => { fail: (callback: (error: unknown) => void) => void };
}
declare global {
  interface Window {
    mediaclip?: {
      hub?: {
        getProjectThumbnailSrc(projectId: string): ProjectThumbnailSrc;
        init: (options: {
          authorization: { scheme: string; token: string };
          renewTokenCallback?: (authorization?: MediaclipAuthorization) => Promise<{
            token?: string;
            redirectUrl?: string;
          }>;
          onError?: (err: unknown) => void;
          region?: string
        }) => void;
        destroy?: (full?: boolean) => void;
        launch: (options: {
          projectId: string;
          authorization: { scheme: string; token: string };
          culture?: string;
          runContext?: Record<string, any>;
          container: HTMLElement;
          renewTokenCallback?: (authorization?: MediaclipAuthorization) => Promise<{
            token?: string;
            redirectUrl?: string;
          }>;
          onError?: (e: unknown) => void;
          onSaveComplete?: () => void;
          region?: string
        }) => Promise<void>;
      };
    };
  }
}

export const loadMediaclipScript = (scriptLoadedRef: BoolRef): Promise<void> => {
  if (scriptLoadedRef.current) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${MEDIACLIP_SCRIPT_URL}"]`
    );
    if (existing) {
      scriptLoadedRef.current = true;
      return resolve();
    }

    const script = document.createElement('script');
    script.src = MEDIACLIP_SCRIPT_URL;
    // script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load script: ${script.src}`));

    document.head.appendChild(script);
  });
};

export const waitForMediaclipHub = (): Promise<void> => {
  return new Promise((resolve) => {
    const start = performance.now();
    const timeoutMs = 10_000;

    const checkHub = () => {
      if (window.mediaclip?.hub) {
        resolve();
      } else if (performance.now() - start < timeoutMs) {
        setTimeout(checkHub, 50);
      } else {
        // Timeout: resolve; launch will handle absence
        resolve();
      }
    };
    checkHub();
  });
};

export const initializeMediaclipHub = (
  token: string,
  mediaclipRegion: string,
  options?: MediaclipHubOptions
): void => {
  const hub = window.mediaclip?.hub;
  if (!hub) {
    console.error('Mediaclip hub not available');
    return;
  }

  hub.init({
    authorization: {
      scheme: MEDIACLIP_AUTH_SCHEME,
      token,
    },
    region: mediaclipRegion,
    renewTokenCallback: options?.renewTokenCallback,
    onError: options?.onError,
  });
};

/**
 * Resolves a usable Mediaclip user token: the one in storage when present, otherwise a
 * freshly issued one (persisted for the next caller).
 *
 * Customization data lives in sessionStorage, so any arrival in a fresh browsing context
 * — or after `clearBadMediaClipSession()` removed the token — has nothing stored. Callers
 * must never hand an empty token to `initializeMediaclip`: the hub cannot initialize
 * without one, and every later `hub.*` call then fails with "Call init() first".
 */
export const ensureMediaclipToken = async (): Promise<string | null> => {
  const stored = getCustomizationData()?.token;
  if (typeof stored === 'string' && stored.trim()) return stored;

  try {
    const response = await sdk.composableCommerce.mediaClip.getUserToken();
    if (response.isError) return null;

    const token = response.data?.userToken;
    if (typeof token !== 'string' || !token.trim()) return null;

    setCustomizationData({
      ...getCustomizationData(),
      token,
      tokenFetchedAt: Date.now(),
    });

    return token;
  } catch (error) {
    console.error('Failed to obtain Mediaclip token', error);
    return null;
  }
};

let initPromise: Promise<string | null> | null = null;
let lastInitToken: string | null = null;
let renewInFlight: Promise<{ token?: string; redirectUrl?: string }> | null = null;

/**
 * Number of mounted components currently relying on the hub. Passive consumers (cart and
 * order thumbnails, the add-to-cart confirmation) only ever called `hub.init()`; nothing
 * destroyed it, so the keep-alive timer kept renewing for the rest of the page's life and
 * failed with "Could not locate iframe" once the designer iframe was gone. See
 * `acquireMediaclipHub`.
 */
const activeHubLeases = new Set<symbol>();
let pendingTeardown: ReturnType<typeof setTimeout> | null = null;

/**
 * Thumbnails unmount and remount constantly (route changes, cart re-renders, list
 * virtualisation). Tearing the hub down the instant the count hits zero would mean a
 * destroy/init churn on every such transition, so wait briefly for a replacement lease.
 */
const HUB_TEARDOWN_GRACE_MS = 5_000;

const cancelPendingTeardown = () => {
  if (pendingTeardown) {
    clearTimeout(pendingTeardown);
    pendingTeardown = null;
  }
};

export const initializeMediaclip = async (
  scriptLoadedRef: BoolRef,
  token: string,
  mediaclipRegion: string,
  options?: MediaclipHubOptions
): Promise<string | null> => {
  if (!token) {
    console.error('No Mediaclip token provided to initializeMediaclip');
    return null;
  }

  if (lastInitToken && lastInitToken !== token) {
    initPromise = null;
  }

  if (!initPromise) {
    lastInitToken = token;
    initPromise = (async () => {
      try {
        await loadMediaclipScript(scriptLoadedRef);
        await waitForMediaclipHub();
        initializeMediaclipHub(token, mediaclipRegion, options);
        return token;
      } catch (error) {
        console.error('Failed to initialize Mediaclip:', error);
        initPromise = null;
        lastInitToken = null;
        return null;
      }
    })();
  }

  return initPromise;
};

/**
 * Tears the hub down completely.
 *
 * `hub.destroy()` with no argument only disposes the launcher; the keep-alive timer
 * started by `hub.init()` keeps polling and renewing the token for the rest of the
 * page's life. Once the designer iframe is gone from the document (navigation away,
 * unmount) that renewal fails with "Could not locate iframe". Passing `true` also
 * stops the keep-alive timer and clears the init container, so the next visit
 * re-initializes with fresh settings.
 */
export const destroyMediaclipHub = () => {
  cancelPendingTeardown();
  activeHubLeases.clear();
  try {
    (window as any).mediaclip?.hub?.destroy?.(true);
  } catch (e) {
    console.error('Failed to destroy MediaClip hub', e);
  }
  initPromise = null;
  lastInitToken = null;
  renewInFlight = null;
};

export const clearBadMediaClipSession = () => {
  cancelPendingTeardown();
  activeHubLeases.clear();
  try {
    const retrievecustomizationData = getCustomizationData();

    if (Object.keys(retrievecustomizationData).length > 0) {
      const data = { ...retrievecustomizationData };
      delete data.token;
      setCustomizationData(data);
    }
  } catch (e) {
    console.error('Failed to clear MediaClip token', e);
  }
  try {
    (window as any).mediaclip?.hub?.destroy?.(true);
  } catch (e) {
    console.error('Failed to destroy MediaClip hub', e);
  }
  initPromise = null;
  lastInitToken = null;
  renewInFlight = null;
};

export type MediaclipHubLease = {
  /** The token the hub was initialized with, or `null` when initialization failed. */
  token: string | null;
  /** Idempotent. Releases this consumer's claim on the hub. */
  release: () => void;
};

const scheduleTeardown = () => {
  cancelPendingTeardown();

  pendingTeardown = setTimeout(() => {
    pendingTeardown = null;
    if (activeHubLeases.size > 0) return;
    destroyMediaclipHub();
  }, HUB_TEARDOWN_GRACE_MS);
};

/**
 * Initializes the hub on behalf of a passive consumer and hands back a `release` the
 * caller must invoke on unmount. The hub stays alive while at least one lease is held and
 * is torn down with `destroy(true)` — which is what stops the keep-alive timer — shortly
 * after the last one is released.
 *
 * Refcounting is what makes this safe for `useMediaClipThumbnail`, which is multi-instance:
 * one thumbnail unmounting must not kill the hub its siblings are still using.
 *
 * The designer page does not use this. It calls `hub.launch()` and owns the teardown of
 * its own iframe directly via `destroyMediaclipHub()`.
 */
export const acquireMediaclipHub = async (
  scriptLoadedRef: BoolRef,
  token: string,
  mediaclipRegion: string,
  options?: MediaclipHubOptions,
): Promise<MediaclipHubLease> => {
  const lease = Symbol('mediaclip-hub-lease');
  activeHubLeases.add(lease);
  cancelPendingTeardown();

  const release = () => {
    // A lease missing from the registry was already released, or was invalidated wholesale
    // by `destroyMediaclipHub()` / `clearBadMediaClipSession()`. Either way it no longer
    // speaks for anything, and must not schedule a teardown: the designer page destroys the
    // hub and then launches its own, so a late release from a thumbnail that unmounted
    // during the route change would otherwise kill the live designer five seconds later.
    if (!activeHubLeases.delete(lease)) return;

    if (activeHubLeases.size === 0) scheduleTeardown();
  };

  try {
    const initializedToken = await initializeMediaclip(scriptLoadedRef, token, mediaclipRegion, options);
    return { token: initializedToken, release };
  } catch (error) {
    release();
    throw error;
  }
};

type RenewResult = { token?: string; redirectUrl?: string };

const RENEW_MAX_ATTEMPTS = 3;
const RENEW_BASE_DELAY_MS = 400;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const persistRenewedToken = (token: string) => {
  setCustomizationData({
    ...getCustomizationData(),
    token,
    tokenFetchedAt: Date.now(),
  });
};

/**
 * The SDK returns the parsed error body (our `{ code, message }`) on a non-2xx, never an
 * HTTP status — so an expired session has to be recognised by code, not by `error.status`.
 */
const AUTH_ERROR_CODES = new Set([
  'Unauthorized',
  'Forbidden',
  'MediaclipHubApiUnauthorizedError',
  'MediaclipHubApiForbiddenError',
]);

const isAuthFailure = (error: unknown): boolean => {
  const code = (error as { code?: string })?.code;
  return typeof code === 'string' && AUTH_ERROR_CODES.has(code);
};

const getLoginRedirectUrl = () =>
  typeof window === 'undefined' ? '/login' : `${window.location.origin}/login`;

/** One renewal round-trip: `mediaclip/renew-token`, falling back to minting a new token. */
const requestRenewedToken = async (): Promise<RenewResult | null> => {
  const response = await sdk.callAction<any>({ actionName: 'mediaclip/renew-token' });

  if (!response.isError) {
    const data = (response as { data: any }).data;
    if (data?.redirectUrl) return { redirectUrl: data.redirectUrl };

    const token = data?.userToken ?? data?.token;
    if (token) {
      persistRenewedToken(token);
      return { token };
    }
    // 200 with neither token nor redirect: fall through to the mint path below.
  } else if (isAuthFailure((response as { error: unknown }).error)) {
    // The storefront session itself is gone. The hub understands a redirect; it does not
    // understand an empty response, which is what used to surface as
    // "Missing `token` in response of renew token request".
    return { redirectUrl: getLoginRedirectUrl() };
  }

  const minted = await sdk.composableCommerce.mediaClip.getUserToken();

  if (!minted.isError) {
    const token = minted.data?.userToken;
    if (typeof token === 'string' && token.trim()) {
      persistRenewedToken(token);
      return { token };
    }
  } else if (isAuthFailure((minted as { error: unknown }).error)) {
    return { redirectUrl: getLoginRedirectUrl() };
  }

  // Transient (timeout / 5xx / network). Signal "retry me".
  return null;
};

const runRenew = async (authorization?: MediaclipAuthorization): Promise<RenewResult> => {
  for (let attempt = 1; attempt <= RENEW_MAX_ATTEMPTS; attempt += 1) {
    try {
      const result = await requestRenewedToken();
      if (result) return result;
    } catch (error) {
      console.warn(`Mediaclip token renewal attempt ${attempt} failed`, error);
    }

    if (attempt < RENEW_MAX_ATTEMPTS) {
      await delay(RENEW_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
  }

  // Every attempt hit a transient failure. Renewal is pre-emptive, so the token we already
  // hold is usually still valid: hand it back and let the hub's keep-alive try again on its
  // next tick, rather than returning `{}` and tripping the hub's "Missing `token`" error.
  const stored = getCustomizationData()?.token;
  if (typeof stored === 'string' && stored.trim()) {
    console.warn('Mediaclip token renewal exhausted retries; reusing the stored token');
    return { token: stored };
  }

  // Storage can be empty while the hub is alive (`clearMediaclipToken()` + a failed
  // re-mint). The hub's own token is always set, so hand that back rather than `{}`.
  const held = authorization?.token;
  if (typeof held === 'string' && held.trim()) {
    console.warn('Mediaclip token renewal exhausted retries and storage is empty; reusing the hub token');
    return { token: held };
  }

  return {};
};

/**
 * Single-flight: the hub keep-alive timer and any component-level recovery can all ask for
 * a renewal at once. Parallel renewals race on the same server-side session token — the
 * loser receives a token the winner has already rotated away, which is the second source of
 * "Missing `token` in response of renew token request".
 */
export const renewTokenCallback = async (authorization?: MediaclipAuthorization): Promise<RenewResult> => {
  if (renewInFlight) return renewInFlight;

  renewInFlight = runRenew(authorization).finally(() => {
    renewInFlight = null;
  });

  return renewInFlight;
};

export type TransformedPhotoInfoMediaClip = {
  type: 'photo';
  height: number;
  id: string;
  autoCrops?: MediaclipAutoCrop[];
  mimeType: string;
  urlPrint: string;
  urlThumb: string;
  versions?: { height: number; url: string; width: number; mimeType: string }[];
  width: number;
  photoSourceName?: string;
  originalUrl?: string;
  originalFilename?: string;
  dateTaken?: string;
};

const getAutoCrops = (photo: TransformedPhotoInfo): MediaclipAutoCrop[] | null => {
  const contentRegion = photo.metadata.contentRegion;
  if (!contentRegion) return null;

  const { x, y, width, height } = contentRegion;
  const left = Math.round(x * photo.width);
  const top = Math.round(y * photo.height);
  const right = Math.round((x + width) * photo.width);
  const bottom = Math.round((y + height) * photo.height);

  return [
    {
      x: left,
      y: top,
      width: right - left,
      height: bottom - top,
    },
  ];
};

export const transformPhotosForMediaclip = (
  photos: OriginalPhotoInfo[],
  options: ImageOptions,
  mediaclipPhotoSource: string,
): TransformedPhotoInfoMediaClip[] => {
  return photos.map((photo) => {
    const converted = convertGoogleImageData(photo, options);
    const autoCrops = getAutoCrops(converted);

    return {
      ...converted,
      versions: getImageVersions(photo, [200, 700, 1200], options)
        .map((version) => ({
          width: version.width,
          height: version.height,
          url: version.url,
          mimeType: photo.type,
        })),
      ...(autoCrops && { autoCrops }),
      photoSourceName: mediaclipPhotoSource,
      originalUrl: converted.urlPrint,
      dateTaken: photo.date,
    };
  });
};

export const transformPhotosForMediaclipBeautyShots = (
  photos: OriginalPhotoInfo[],
  options: ImageOptions,
): MediaclipBeautyshotPhoto[] => {
  return photos.map((photo) => {
    const converted = convertGoogleImageData(photo, options);
    const autoCrops = getAutoCrops(converted);

    return {
      info: {
        url: converted.urlPrint,
        width: converted.width,
        height: converted.height,
        ...(autoCrops && { autoCrops }),
      },
    };
  });
};
